import { Building2, X } from "lucide-react";
import { useEffect } from "react";

function ViewEmployeePermissionById({
  viewPermissionData,
  setViewPermissionData,
}) {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setViewPermissionData(null);
      }
    };

    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "auto";
    };
  }, [setViewPermissionData]);
  if (!viewPermissionData) return null;

  const closeModal = () => setViewPermissionData(null);

  const formatDate = (date) => (date ? new Date(date).toLocaleString() : "—");

  const { permissionId, dashboard, system, createdAt, employee } =
    viewPermissionData;

  const {
    id,
    title,
    name,
    email,
    role,
    employeeReferenceId,
    mobile,
    gender,
    dob,
    imageUrl,
    address,
  } = employee || {};

  return (
    <div className="fixed overflow-hidden inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        onClick={closeModal}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      />

      {/* Modal */}
      <div className="relative bg-white w-[95%] max-h-[calc(100%-80px)] overflow-y-auto max-w-3xl rounded-2xl shadow-2xl p-8 animate-in fade-in zoom-in-95">
        {/* Header */}
        <div className="relative flex items-center justify-between bg-gradient-to-r from-blue-100 to-white p-2 sm:p-6 rounded-2xl border border-gray-100 mb-6">
          <div className="flex items-center gap-5">
            {/* Image / Icon */}
            <div className="sm:w-24 sm:h-24 w-16 h-16 rounded-2xl overflow-hidden shadow-md border bg-white flex items-center justify-center">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt="Branch"
                  className="w-full h-full object-cover"
                />
              ) : (
                <Building2 className="w-10 h-10 text-blue-500" />
              )}
            </div>

            {/* Title */}
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400">
                Employee Permission
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
                {title} {name}
              </h2>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={closeModal}
            className="absolute top-4 -right-4 sm:right-4 bg-white border rounded-full p-2 text-gray-400 hover:text-red-500 hover:shadow-md transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
          <Detail label="Employee Id" value={employeeReferenceId} />
          <Detail label="Email" value={email} />
          <Detail label="Mobile" value={mobile} />
          <Detail label="Role" value={role} />
          <Detail label="Gender" value={gender} />
          <Detail label="Date of Birth" value={dob} />

          <div className="sm:col-span-2">
            <Detail label="Full Address" value={address} />
          </div>

          <StatusBadge
            label="Dashboard Access"
            value={dashboard}
            activeText="Allowed"
            inactiveText="Not Allowed"
            activeColor="bg-green-100 text-green-700"
            inactiveColor="bg-red-100 text-red-600"
          />

          <StatusBadge
            label="System Access"
            value={system}
            activeText="Allowed"
            inactiveText="Not Allowed"
            activeColor="bg-green-100 text-green-700"
            inactiveColor="bg-red-100 text-red-600"
          />

          <Detail label="Deactivated At" value={formatDate(createdAt)} />
        </div>

        {/* Footer */}
        <div className="mt-8 text-right">
          <button
            onClick={closeModal}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div>
    <p className="text-gray-500">{label}</p>
    <p className="font-medium text-gray-800">{value || "—"}</p>
  </div>
);

const StatusBadge = ({
  label,
  value,
  activeText,
  inactiveText,
  activeColor,
  inactiveColor,
}) => (
  <div>
    <p className="text-gray-500">{label}</p>
    <span
      className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
        value ? activeColor : inactiveColor
      }`}
    >
      {value ? activeText : inactiveText}
    </span>
  </div>
);

export default ViewEmployeePermissionById;
